import React, { useEffect, useState, useMemo } from "react";
import { Container, Table, Button, Row, Col, Badge, Card, Form, Pagination, Offcanvas, } from "react-bootstrap";
import {
  FiSearch, FiFilter, FiDownload, FiHome,
  FiChevronRight,
} from "react-icons/fi";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { useDispatch } from "react-redux";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";

import { getAttendence } from "../../store/slices/attendence";
import { getClassteacher } from "../../store/slices/classteacher";

export default function Attendance() {
  const dispatch = useDispatch();

  const [classTeachers, setClassTeachers] = useState([]);

  const [teacherId, setTeacherId] = useState("");
  const [standardId, setStandardId] = useState("");
  const [sectionId, setSectionId] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [attendanceData, setAttendanceData] = useState([]);
  const [showTable, setShowTable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [showFilterDrawer, setShowFilterDrawer] = useState(false);

  const [searchName, setSearchName] = useState("");
  const [filterDate, setFilterDate] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ------- > fetch class teachers */
  useEffect(() => {
    const fetchClassTeachers = async () => {
      try {
        const res = await dispatch(getClassteacher()).unwrap();
        setClassTeachers(res?.data || []);
      } catch {
        toast.error("Failed to fetch class teachers");
      }
    };
    fetchClassTeachers();
  }, [dispatch]);

  /* options <--------------- */
  const standardOptions = useMemo(
    () => classTeachers.filter((ct) => ct.teacherId?._id === teacherId),
    [teacherId, classTeachers]
  );

  const sectionOptions = standardOptions;

  /* ------- > fetch attendance */
  const fetchAttendance = async () => {
    if (!teacherId || !standardId || !sectionId) {
      toast.error("Please select teacher, standard and section");
      return;
    }

    if (!fromDate || !toDate) {
      toast.error("Please select date range");
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error("From date cannot be greater than To date");
      return;
    }

    setIsLoading(true);
    setShowTable(false);
    setCurrentPage(1);

    try {
      const res = await dispatch(
        getAttendence({ teacherId, standardId, sectionId, fromDate, toDate })
      ).unwrap();

      setAttendanceData(res?.data?.attendanceData || []);
      setShowTable(true);
      setShowFilterDrawer(false);
    } catch {
      toast.error("Failed to fetch attendance");
    } finally {
      setIsLoading(false);
    }
  };

  /* table rows < ---------- */
  const tableData = useMemo(() => {
    let rows = [];

    attendanceData.forEach((item) => {
      item.students?.forEach((stu) => {
        rows.push({
          id: `${item._id}-${stu.studentId?._id}`,
          date: item.date,
          name: stu.studentId?.name || "",
          status: stu.status,
        });
      });
    });

    if (searchName) {
      rows = rows.filter((r) =>
        r.name.toLowerCase().includes(searchName.toLowerCase())
      );
    }

    if (filterDate) {
      rows = rows.filter(
        (r) =>
          new Date(r.date).toISOString().split("T")[0] === filterDate
      );
    }

    return rows;
  }, [attendanceData, searchName, filterDate]);

  /* -------- > pagination */
  const totalPages = Math.ceil(tableData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedData = tableData.slice(startIndex, startIndex + itemsPerPage);

  /* clear filter < -------- */
  const clearFilters = () => {
    setSearchName("");
    setFilterDate("");
  };

  /* -------- > export */
  const handleExport = () => {
    if (!tableData.length) {
      toast.error("No data to export");
      return;
    }

    const headers = ["Date", "Student Name", "Status"];

    const rows = tableData.map((r) => [
      new Date(r.date).toLocaleDateString("en-GB"),
      r.name,
      r.status === 1 ? "Present" : "Absent",
    ]);

    const csv =
      "data:text/csv;charset=utf-8," +
      [headers, ...rows].map((r) => r.join(",")).join("\n");

    const link = document.createElement("a");
    link.href = csv;
    link.download = "attendance_report.csv";
    link.click();
  };

  return (
    <Container fluid>
      {/* --------> Header */}
      <div className="page-header mb-3">
        <div className="breadcrumb-section">

          <div className="breadcrumb-left">
            <div className="home-icon">
              <FiHome />
            </div>

            <FiChevronRight className="separator" />

            <Link to="/institute/home">Home</Link>

            <FiChevronRight className="separator" />

            <span className="active">Attendance Overview</span>
          </div>

        </div>
      </div>

      {/* filter card < ------ */}
      {!showTable && (
        <Card className="mb-4" style={{ border: "none" }}>
          <Card.Body>
            <Card.Title className="text-center">Get Attendance</Card.Title>

            <Row>
              <Col md={6} className="mb-3">
                <Form.Label>Teacher</Form.Label>
                <Form.Select
                  value={teacherId}
                  onChange={(e) => {
                    setTeacherId(e.target.value);
                    setStandardId("");
                    setSectionId("");
                  }}
                >
                  <option value="">Select Teacher</option>
                  {classTeachers.map((ct) => (
                    <option key={ct.teacherId?._id} value={ct.teacherId?._id}>
                      {ct.teacherId?.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>Standard</Form.Label>
                <Form.Select
                  value={standardId}
                  disabled={!teacherId}
                  onChange={(e) => setStandardId(e.target.value)}
                >
                  <option value="">Select Standard</option>
                  {standardOptions.map((ct) => (
                    <option key={ct.standardId?._id} value={ct.standardId?._id}>
                      {ct.standardId?.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>Section</Form.Label>
                <Form.Select
                  value={sectionId}
                  disabled={!standardId}
                  onChange={(e) => setSectionId(e.target.value)}
                >
                  <option value="">Select Section</option>
                  {sectionOptions.map((ct) => (
                    <option key={ct.sectionId?._id} value={ct.sectionId?._id}>
                      {ct.sectionId?.name}
                    </option>
                  ))}
                </Form.Select>
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                />
              </Col>

              <Col md={6} className="mb-3">
                <Form.Label>To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                />
              </Col>

              <Col md={12}>
                <button className="btn-submit w-100" onClick={fetchAttendance}>
                  Get Attendance
                </button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}


      {showTable && (
        <Card style={{ border: "none" }}>
          <Card.Body>

            <Row className="align-items-center mb-3">

              <Col md={6}>
                <div className="staff-search d-inline-flex">
                  <FiSearch />
                  <input
                    placeholder="Search by student name"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                  />
                </div>
              </Col>


              <Col md={6} className="d-flex justify-content-end gap-2">
                <button
                  className="btn-cancel d-flex align-items-center gap-2"
                  onClick={() => setShowFilterDrawer(true)}
                >
                  <FiFilter />
                  <span className="d-none d-md-inline">Filter</span>
                </button>

                <button
                  className="btn-submit d-flex align-items-center gap-2"
                  onClick={handleExport}
                >
                  <FiDownload />
                  <span className="d-none d-md-inline">Export</span>
                </button>
              </Col>
            </Row>

            <Table responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Name</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length ? (
                  paginatedData.map((row, i) => (
                    <tr key={row.id}>
                      <td>{startIndex + i + 1}</td>
                      <td>{new Date(row.date).toLocaleDateString("en-GB")}</td>
                      <td>{row.name}</td>
                      <td>
                        <Badge bg={row.status === 1 ? "success" : "danger"}>
                          {row.status === 1 ? "Present" : "Absent"}
                        </Badge>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>

            {/* pagination */}
            <Pagination className="justify-content-end">
              <Button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => p - 1)}
                style={{ background: "transparent", color: "#8451ff", borderRadius: "15px" }}
              >
                <IoIosArrowBack />
              </Button>

              <Button active disabled style={{ borderRadius: "20px" }}>
                {currentPage}
              </Button>

              <Button
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage((p) => p + 1)}
                style={{ background: "transparent", color: "#8451ff", borderRadius: "15px" }}
              >
                <IoIosArrowForward />
              </Button>
            </Pagination>
          </Card.Body>
        </Card>
      )}


      <Offcanvas
        show={showFilterDrawer}
        onHide={() => setShowFilterDrawer(false)}
        placement="end"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>Change Attendance Filter</Offcanvas.Title>
        </Offcanvas.Header>

        <Offcanvas.Body>
          <Form.Select
            className="mb-3"
            value={teacherId}
            onChange={(e) => {
              setTeacherId(e.target.value);
              setStandardId("");
              setSectionId("");
            }}
          >
            <option value="">Select Teacher</option>
            {classTeachers.map((ct) => (
              <option key={ct.teacherId?._id} value={ct.teacherId?._id}>
                {ct.teacherId?.name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="mb-3"
            value={standardId}
            disabled={!teacherId}
            onChange={(e) => setStandardId(e.target.value)}
          >
            <option value="">Select Standard</option>
            {standardOptions.map((ct) => (
              <option key={ct.standardId?._id} value={ct.standardId?._id}>
                {ct.standardId?.name}
              </option>
            ))}
          </Form.Select>

          <Form.Select
            className="mb-3"
            value={sectionId}
            disabled={!standardId}
            onChange={(e) => setSectionId(e.target.value)}
          >
            <option value="">Select Section</option>
            {sectionOptions.map((ct) => (
              <option key={ct.sectionId?._id} value={ct.sectionId?._id}>
                {ct.sectionId?.name}
              </option>
            ))}
          </Form.Select>

          <Form.Control
            type="date"
            className="mb-3"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />

          <Form.Control
            type="date"
            className="mb-3"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />

          <div className="d-flex gap-2">
            <button className="btn-submit w-100" onClick={fetchAttendance}>
              Apply
            </button>
            <button className="btn-cancel w-100" onClick={() => setShowFilterDrawer(false)}>
              Cancel
            </button>
          </div>
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
}
